import React, { ReactElement, useCallback, useState, useEffect } from "react";
import { ItemListHeader as StyledItemListHeader, ItemListHeaderTitle, IconContainer, LottieContainer } from "../styles";

// test ids
import { CART_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

// utils
import { isEmpty, getCloudinaryRawUrl, TEAM_LINE } from "@swiggy-private/js-utils";
import { ElementVisibilitySensor } from "@swiggy-private/dls-web-core/dist/components/ElementVisibilitySensor";
import { LottieAnimation } from "@swiggy-private/core-ui";

interface ItemListHeaderProps {
    header: {
        title: string;
        subtitle: string;
        subtitleColor: string;
        icon?: React.ReactNode;
        lottieId?: string;
        backgroundColor?: string;
        showAnimation?: boolean;
    };
    titleColor?: keyof IColors;
    onImpression?: () => void;
}
/**
 * Generates bolder text for given template literals
 * You [[saved Rs 50]] on this order
 * Will return the same text but text will be bolder which is wrapped in "[[" & "]]"
 * @param {string} text
 * @returns {string} text
 */
export const getBoldText = (text: string): string =>
    text.replace("[[", `<span style="font-weight: 600;">`).replace("]]", "</span>");

export function ItemListHeader({ header, titleColor, onImpression }: ItemListHeaderProps): ReactElement {
    const _titleColor = !isEmpty(titleColor) ? titleColor : "positive";
    const isAnimation = header?.showAnimation ?? false;
    const hasLottie = !isEmpty(header?.lottieId);
    const [lottieComplete, setLottieComplete] = useState(!hasLottie);

    // Reset lottieComplete when hasLottie changes
    useEffect(() => {
        setLottieComplete(!hasLottie);
    }, [hasLottie]);

    const onVisibilityChange = useCallback(
        (isVisible: boolean) => {
            if (isVisible && onImpression) {
                onImpression();
            }
        },
        [onImpression],
    );

    const onLottieComplete = useCallback(() => {
        setLottieComplete(true);
    }, []);

    // Start animations only after lottie completes (or immediately if no lottie)
    const startAnimation = lottieComplete;

    return (
        <ElementVisibilitySensor senseOnce={true} onVisibilityChange={onVisibilityChange}>
            <StyledItemListHeader
                isAnimation={isAnimation}
                backgroundColor={header?.backgroundColor}
                hasLottie={hasLottie}
            >
                {header?.icon && <IconContainer>{header?.icon}</IconContainer>}
                {hasLottie && (
                    <LottieContainer>
                        <LottieAnimation
                            fileName={getCloudinaryRawUrl(header.lottieId ?? "", {}, { teamLine: TEAM_LINE.PORTAL })}
                            visible={true}
                            playLottie={true}
                            autoplay={true}
                            loop={false}
                            backwardSpeed={1}
                            forwardSpeed={1}
                            playIfVisible={false}
                            preserveAspect="xMidYMid meet"
                            onPlayComplete={onLottieComplete}
                        />
                    </LottieContainer>
                )}
                {lottieComplete && (
                    <ItemListHeaderTitle
                        variant="V2.overline.neutral.bold"
                        color={_titleColor}
                        dangerouslySetInnerHTML={{ __html: getBoldText(header.title) }}
                        data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_HEADER}
                        isAnimation={isAnimation}
                        startAnimation={startAnimation}
                        hasLottie={hasLottie}
                    />
                )}
            </StyledItemListHeader>
        </ElementVisibilitySensor>
    );
}
